/**
 * Datenzugriff hinter einer Schnittstelle.
 *
 * Solange kein Server existiert, läuft alles lokal im Browser (localStorage).
 * Sobald osm.kgva.ch eine API hat, genügt VITE_API_BASE in .env – die UI
 * bleibt unverändert, weil sie nur gegen dieses Interface arbeitet:
 *
 *   auth:      getSession, register, login, logout
 *   favorites: list, add, remove, update
 *   prefs:     load, save
 *   tracks:    list, get, add, update, remove
 */

const API_BASE = import.meta.env?.VITE_API_BASE || '';
const LS_PROFILE = 'kgva.profile';
const LS_FAVORITES = 'kgva.favorites';
const LS_PREFS = 'kgva.prefs';

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function write(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Privater Modus / voller Speicher: Daten bleiben nur in dieser Sitzung */
  }
}

const scope = (profile) => (profile ? `${LS_FAVORITES}.${profile.id}` : `${LS_FAVORITES}.guest`);
const profilId = (profile) => (profile ? profile.id : 'guest');

/* ------------------------------------------------- Touren im Browser --- */

/**
 * Aufgezeichnete Touren liegen in IndexedDB, nicht in `localStorage`.
 *
 * Eine Wanderung von drei Stunden sind leicht zehntausend Punkte; in
 * `localStorage` (rund 5 MB für alles zusammen) wären nach ein paar Touren
 * auch die Favoriten verloren. IndexedDB hat Platz und speichert Objekte,
 * statt sie durch JSON zu pressen.
 *
 * Zwei Ablagen: die Merkmale einer Tour und, getrennt davon, ihre Punkte.
 * Die Liste im Panel braucht nur die Merkmale - sie soll nicht jedes Mal
 * hunderttausend Koordinaten durch den Speicher ziehen.
 */
const DB = 'kgva';
const DB_VERSION = 1;
const TOUREN = 'touren';
const PUNKTE = 'tourpunkte';

function datenbank() {
  return new Promise((ok, fehler) => {
    if (!('indexedDB' in globalThis)) { fehler(new Error('Kein Speicher für Touren vorhanden.')); return; }
    const anfrage = indexedDB.open(DB, DB_VERSION);
    anfrage.onupgradeneeded = () => {
      const d = anfrage.result;
      if (!d.objectStoreNames.contains(TOUREN)) d.createObjectStore(TOUREN, { keyPath: 'id' });
      if (!d.objectStoreNames.contains(PUNKTE)) d.createObjectStore(PUNKTE, { keyPath: 'id' });
    };
    anfrage.onsuccess = () => ok(anfrage.result);
    anfrage.onerror = () => fehler(anfrage.error);
  });
}

function lauf(store, modus, arbeit) {
  return datenbank().then((d) => new Promise((ok, fehler) => {
    const t = d.transaction(store, modus);
    const anfrage = arbeit(t.objectStore(store));
    t.oncomplete = () => ok(anfrage?.result);
    t.onerror = () => fehler(t.error);
    t.onabort = () => fehler(t.error);
  }));
}

const localTracks = {
  async list(profile) {
    const alle = (await lauf(TOUREN, 'readonly', (s) => s.getAll())) || [];
    return alle
      .filter((t) => t.profil === profilId(profile))
      .sort((a, b) => (b.begonnen || 0) - (a.begonnen || 0));
  },
  async get(profile, id) {
    const tour = await lauf(TOUREN, 'readonly', (s) => s.get(id));
    if (!tour || tour.profil !== profilId(profile)) return null;
    const p = await lauf(PUNKTE, 'readonly', (s) => s.get(id));
    return { ...tour, punkte: p?.punkte || [] };
  },
  async add(profile, tour) {
    const { punkte = [], ...merkmale } = tour;
    const eintrag = {
      id: crypto.randomUUID(),
      profil: profilId(profile),
      createdAt: new Date().toISOString(),
      ...merkmale
    };
    await lauf(TOUREN, 'readwrite', (s) => s.put(eintrag));
    await lauf(PUNKTE, 'readwrite', (s) => s.put({ id: eintrag.id, punkte }));
    return eintrag;
  },
  async update(profile, id, patch) {
    const tour = await lauf(TOUREN, 'readonly', (s) => s.get(id));
    if (!tour || tour.profil !== profilId(profile)) return null;
    const neu = { ...tour, ...patch, id, profil: tour.profil };
    await lauf(TOUREN, 'readwrite', (s) => s.put(neu));
    return neu;
  },
  async remove(profile, id) {
    await lauf(TOUREN, 'readwrite', (s) => s.delete(id));
    await lauf(PUNKTE, 'readwrite', (s) => s.delete(id));
  }
};

/* ---------------------------------------------------------------- lokal --- */

const localBackend = {
  mode: 'local',

  auth: {
    async getSession() {
      return read(LS_PROFILE, null);
    },
    async register(name) {
      return this.login(name);
    },
    async login(name) {
      const clean = String(name || '').trim();
      if (!clean) throw new Error('Bitte einen Profilnamen angeben.');
      const profile = { id: slug(clean), name: clean, local: true };
      write(LS_PROFILE, profile);
      return profile;
    },
    async logout() {
      try {
        localStorage.removeItem(LS_PROFILE);
      } catch { /* ignorieren */ }
    }
  },

  favorites: {
    async list(profile) {
      return read(scope(profile), []);
    },
    async add(profile, fav) {
      const items = read(scope(profile), []);
      const entry = { id: crypto.randomUUID(), createdAt: new Date().toISOString(), ...fav };
      items.unshift(entry);
      write(scope(profile), items);
      return entry;
    },
    async update(profile, id, patch) {
      const items = read(scope(profile), []).map((f) => (f.id === id ? { ...f, ...patch } : f));
      write(scope(profile), items);
      return items.find((f) => f.id === id);
    },
    async remove(profile, id) {
      write(scope(profile), read(scope(profile), []).filter((f) => f.id !== id));
    }
  },

  prefs: {
    async load(profile) {
      return read(`${LS_PREFS}.${profile ? profile.id : 'guest'}`, null);
    },
    async save(profile, prefs) {
      write(`${LS_PREFS}.${profile ? profile.id : 'guest'}`, prefs);
    }
  },

  tracks: localTracks
};

/* ----------------------------------------------------------------- HTTP --- */

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error((await res.text()) || `Fehler ${res.status}`);
  return res.status === 204 ? null : res.json();
}

const httpBackend = {
  mode: 'server',

  auth: {
    getSession: () => api('/api/auth/session'),
    register: (email, password, name) =>
      api('/api/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name }) }),
    login: (email, password) =>
      api('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
    logout: () => api('/api/auth/logout', { method: 'POST' })
  },

  favorites: {
    list: () => api('/api/favorites').then((r) => r || []),
    add: (_profile, fav) => api('/api/favorites', { method: 'POST', body: JSON.stringify(fav) }),
    update: (_profile, id, patch) =>
      api(`/api/favorites/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (_profile, id) => api(`/api/favorites/${id}`, { method: 'DELETE' })
  },

  prefs: {
    load: () => api('/api/prefs'),
    save: (_profile, prefs) => api('/api/prefs', { method: 'PUT', body: JSON.stringify(prefs) })
  },

  tracks: {
    list: () => api('/api/tracks').then((r) => r || []),
    get: (_profile, id) => api(`/api/tracks/${id}`),
    add: (_profile, tour) => api('/api/tracks', { method: 'POST', body: JSON.stringify(tour) }),
    update: (_profile, id, patch) =>
      api(`/api/tracks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (_profile, id) => api(`/api/tracks/${id}`, { method: 'DELETE' })
  }
};

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'profil';
}

export const backend = API_BASE ? httpBackend : localBackend;
export const isLocalMode = backend.mode === 'local';
